/**
 * Vormtaal van de publieke site (docs/VORMGEVING.md › Bouwstenen).
 *
 * Ritme: elke sectie één toon (`tones`). Diepte uit klei en blush, nooit uit zwart; hoogstens
 * één à twee klei-vlakken per pagina.
 * Contrast (AA, gecontroleerd): ink en muted op sand/shell/blush ≥ 4,9:1; shell op clay 5,6:1.
 * Op clay dus nooit `text-muted` of kleine `text-blush`; blush alleen voor grote koppen.
 * Scheidingslijnen altijd haarlijnen (`border-ink/15` of `border-line`), nooit zware inktlijnen.
 *
 * De letterstijlen zelf staan als klassen in index.css (`type-page`, `type-section`, …), zodat ze
 * in elk bestand zonder import te gebruiken zijn. Deze namen blijven voor bestaande code.
 */

/** Sectiekop (h2). */
export const sectionHeading = 'type-section'

/** Paginakop (h1) op elke pagina: één maat, zodat geen pagina harder roept dan de andere. */
export const heroTitle = 'type-page text-ink'

/** Kleinere kop (h2) voor afsluiters en losse blokken: één stap onder de sectiekop. */
export const closingHeading = 'type-sub'

/**
 * Mijn omgeving (docs/VORMGEVING.md §6): één kopmaat-reeks voor de hele portal.
 * `accountTitle` = de paginakop (h1), `accountHeading` = de kop van een blok (h2).
 */
export const accountTitle = 'font-display text-[2.2rem] font-light leading-[1.05] text-ink sm:text-[2.75rem]'
export const accountHeading = 'font-display text-[1.5rem] font-light leading-tight text-ink sm:text-[1.75rem]'

/**
 * De vijf tonen van een sectie, met de tekstkleur die erbij hoort. Gebruik ze via
 * `shop/shared/Section.tsx`; nooit een losse achtergrond plus tekstkleur zelf combineren.
 */
export const tones = {
  sand: 'bg-sand text-ink',
  shell: 'bg-shell text-ink',
  blush: 'bg-blush text-ink',
  sage: 'bg-sage text-shell',
  clay: 'bg-clay text-shell',
} as const
export type Tone = keyof typeof tones

/** Verticale ruimte van een sectie: `default` voor hoofdblokken, `compact` voor afsluiters. */
export const sectionPad = {
  default: 'py-20 sm:py-24 lg:py-32',
  compact: 'py-20 sm:py-24',
  none: '',
} as const
export type SectionPad = keyof typeof sectionPad

/** De inhoudsbreedte met zijmarge, overal gelijk. */
export const contentWidth = 'mx-auto max-w-content px-6 lg:px-10'

/**
 * Vastgezette delen (sticky) staan altijd net onder de header: de header verschijnt bij omhoog
 * scrollen en zet dan `--header-offset` op zijn hoogte (0 als hij weg is). Zo verdwijnt een
 * klachtenbalk of scène nooit onder de header. Zelfde duur als het in- en uitschuiven van de header.
 */
export const belowHeader = 'top-[var(--header-offset,0px)] transition-[top] duration-300 ease-soft motion-reduce:transition-none'

/** Klein regeltje boven een kop. */
export const eyebrowCls = 'text-[14px] tracking-[0.01em]'

/** Rustige tekstlink met pijl, in de kleur van de sectie; 44px hoog om te tikken. */
export const quietLink = 'inline-flex min-h-[44px] items-center gap-2 text-[15px] underline-offset-[6px] transition-colors duration-150 ease-soft hover:underline'
